import { useEffect, useRef, useState } from 'react';

/**
 * @param {Object} config
 * @param {'up' | 'down'} config.direction - Direction of progress ('up' = 0→100, 'down' = 100→0)
 * @param {Function} [config.onComplete] - Callback when progress reaches the end
 * @param {number} [config.speed] - Interval speed in milliseconds
 * @param {number} [config.interval=100] - How often to update the progress (in ms)
 * @returns {{ progress: number, start: Function, stop: Function, reset: Function }}
 */
const useRefreshCycle = ({direction, onComplete, duration, interval = 100}) => {
    const initial = direction === 'down' ? 100 : 0;
    const [progress, setProgress] = useState(initial);
    const intervalRef = useRef(null);

    const step = (100 * interval) / duration; // how much to change each interval

    const stop = () => {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
    };

    const reset = () => {
        stop();
        setProgress(initial);
    };

    const start = () => {
        if (intervalRef.current) return;

        intervalRef.current = setInterval(() => {
            setProgress(prev => {
                const next = direction === 'down' ? prev - step : prev + step;
                const limitReached = direction === 'down' ? next <= 0 : next >= 100;

                if (limitReached) {
                    stop();
                    setProgress(direction === 'down' ? 0 : 100);
                    onComplete?.();
                }

                return limitReached ? (direction === 'down' ? 0 : 100) : next;
            });
        }, interval);
    };

    useEffect(() => stop, []);

    return { progress, start, stop, reset };
}

export default useRefreshCycle;